import React from 'react'

import LockIcon from '@mui/icons-material/Lock'
import { Button, InputAdornment, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { SUBMITTED_IDEAS } from 'constants/routes'

import { BaseTextField } from 'components/Form'
import WarningMessage from 'components/WarningMessage/WarningMessage'

import { typography } from 'theme/typography'

import { commonStyles } from 'styles/common.styles'

import { styles } from './Section1.styles'

export const TEST_ID = 'round-robin-section1'

export type Props = {
	companyIdea: string
	companyName: string
	createPrompt: () => void
	editable: boolean
	generatePromptsDisabled: () => boolean
	isLoading: boolean
	navigateTo: (path: string) => void
	resetAllField: () => void
	setCompanyIdea: (val: string) => void
	setCompanyName: (val: string) => void
}

const Section1 = ({
	companyName,
	setCompanyName,
	companyIdea,
	setCompanyIdea,
	generatePromptsDisabled,
	resetAllField,
	isLoading,
	navigateTo,
	createPrompt,
	editable,
}: Props) => {
	React.useEffect(() => {
		const storedCompany = localStorage.getItem('innovationCompany')
		const storedIdea = localStorage.getItem('companyIdea')
		if (storedCompany) setCompanyName(storedCompany)
		if (storedIdea) setCompanyIdea(storedIdea)
	}, [setCompanyName, setCompanyIdea])
	React.useEffect(() => {
		const debounce = setTimeout(() => {
			localStorage.setItem('innovationCompany', companyName)
			localStorage.setItem('companyIdea', companyIdea)
		}, 200)
		return () => clearTimeout(debounce)
	}, [companyName, companyIdea])
	return (
		<>
			<Typography
				variant={'overline'}
				sx={{ ...commonStyles.header, ...styles.header }}
			>
				1. INNOVATION TOPIC
			</Typography>
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display={'flex'}
					flexDirection={'column'}
					rowGap={'3rem'}
					flexGrow="1"
				>
					<Typography
						sx={commonStyles.header}
						data-testid={`${TEST_ID}__company-brainstorm-prompt`}
					>
						What company are you brainstorming about today?{' '}
						<Box component="span" sx={typography.asterisk}>
							*
						</Box>
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
							required: false,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-name-input`}
						label="Enter company name here."
						value={companyName}
						onChangeValue={(val) => setCompanyName(val)}
						required
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiInputBase-input': {
								boxSizing: 'content-box',
								height: '100%',
							},
							...styles.textField,
						}}
					/>
					<Typography sx={commonStyles.header}>
						IMPORTANT: This game is unique in that it’s designed to
						help you refine and iterate on existing ideas. Enter an
						idea to refine below, or review your submitted ideas
						first.
					</Typography>
					<Box display="flex" flexDirection="row" gap="2rem">
						<Button
							color="secondary"
							variant="outlined"
							data-testid={`${TEST_ID}__view-submitted-ideas-button`}
							onClick={() => navigateTo(SUBMITTED_IDEAS)}
						>
							VIEW SUBMITTED IDEAS
						</Button>
					</Box>
					<Typography
						color="black"
						sx={{ opacity: '87%' }}
						data-testid={`${TEST_ID}__summarize-prompt`}
					>
						Summarize the idea you want to refine (within 5-10
						words).{' '}
						<Box component="span" sx={typography.asterisk}>
							*
						</Box>
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
							required: false,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-idea-input`}
						label="Summarize in 5-10 words"
						onChangeValue={(val) => setCompanyIdea(val)}
						value={companyIdea}
						required
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiInputBase-input': {
								boxSizing: 'content-box',
								height: '100%',
							},
							...styles.textField,
						}}
					/>
					<WarningMessage prompt={companyIdea} />
					<Box display="flex" flexDirection="row" gap="2rem">
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__create-prompt-button`}
							disabled={generatePromptsDisabled() || isLoading}
							onClick={createPrompt}
						>
							START IDEATING
						</Button>
						<Button
							disabled={!editable}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__reset-all-button`}
							onClick={resetAllField}
						>
							RESET ALL FIELDS
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	)
}
export default Section1
